// frontend/src/components/dashboard/pos/components/MobileCartDrawer.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Paper,
  Typography,
  Box,
  IconButton,
  Divider,
  Stack,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  List,
  Button,
  useTheme,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Close,
  DeleteOutline,
  ShoppingCart,
  Add,
  Remove,
  ArrowBack,
  ClearAll,
  Devices as DeviceIcon,
  TableRestaurant as TableIcon,
  Person as PersonIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { commonStyles } from '../styles/posStyles';
import { usePOSSettings } from '../../pages/posconfig/hooks/usePOSSettings'; // ✅ FIXED: Updated path

const MotionDiv = motion.div;

const MobileCartDrawer = ({
  open,
  onClose,
  cartItems,
  onIncreaseQuantity,
  onDecreaseQuantity,
  onRemoveFromCart,
  onClearCart,
  calculateTotal,
  deviceNumber,
  onDeviceNumberChange,
  onPayment,
  isOnline,
  fullScreen = false,
  hasSafeArea = false
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  // ✅ NEW: Use POS Settings
  const { 
    settings, 
    calculateCartTotals 
  } = usePOSSettings();

  // ✅ NEW: Enhanced metadata state
  const [orderMetadata, setOrderMetadata] = useState({
    deviceNumber: deviceNumber || '',
    tableNumber: '',
    customerName: '',
    notes: ''
  });

  // ✅ NEW: Dynamic add-on states
  const [addOnStates, setAddOnStates] = useState({});

  // Initialize add-on states from settings
  useEffect(() => {
    if (settings?.cartAddOns) {
      const initialStates = {};
      
      // ✅ SAFE: Handle both array and object formats
      let cartAddOnsArray;
      if (Array.isArray(settings.cartAddOns)) {
        cartAddOnsArray = settings.cartAddOns;
      } else if (typeof settings.cartAddOns === 'object') {
        cartAddOnsArray = Object.values(settings.cartAddOns);
      } else {
        cartAddOnsArray = [];
      }
      
      cartAddOnsArray.forEach(addOn => {
        initialStates[addOn.id] = addOn.enabled;
      });
      setAddOnStates(initialStates);
    }
  }, [settings?.cartAddOns]);

  // Update legacy props when metadata changes
  useEffect(() => {
    if (onDeviceNumberChange) {
      onDeviceNumberChange({ target: { value: orderMetadata.deviceNumber } });
    }
  }, [orderMetadata.deviceNumber, onDeviceNumberChange]);

  // ✅ NEW: Calculate totals using POS settings
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totals = settings ? calculateCartTotals(subtotal) : calculateTotal();

  // ✅ NEW: Get enabled cart add-ons
  const enabledAddOns = (() => {
    if (!settings?.cartAddOns) return [];
    
    if (Array.isArray(settings.cartAddOns)) {
      return settings.cartAddOns.filter(addOn => 
        addOn.enabled && addOn.visible && addOnStates[addOn.id]
      );
    } else if (typeof settings.cartAddOns === 'object') {
      return Object.values(settings.cartAddOns).filter(addOn => 
        addOn.enabled && addOn.visible && addOnStates[addOn.id]
      );
    }
    
    return [];
  })();

  // ✅ NEW: Get enabled metadata fields
  const enabledMetadataFields = Object.entries(settings?.cartMetadata || {})
    .filter(([, config]) => config.enabled && config.visible !== false);
    
  console.log('📱 Mobile Cart metadata for display:', {
    totalConfigured: Object.keys(settings?.cartMetadata || {}).length,
    enabledAndVisible: enabledMetadataFields.length,
    fields: enabledMetadataFields.map(([name, config]) => ({ name, label: config.label }))
  });

  // ✅ NEW: Handle add-on toggle
  const handleAddOnToggle = (addOnId, checked) => {
    setAddOnStates(prev => ({
      ...prev,
      [addOnId]: checked
    }));
  };

  // ✅ NEW: Handle metadata change
  const handleMetadataChange = (fieldName, value) => {
    setOrderMetadata(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // ✅ NEW: Get field icon
  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'deviceNumber':
        return <DeviceIcon fontSize="small" />;
      case 'tableNumber':
        return <TableIcon fontSize="small" />;
      case 'customerName':
        return <PersonIcon fontSize="small" />;
      case 'notes':
        return <NotesIcon fontSize="small" />;
      default:
        return <DeviceIcon fontSize="small" />;
    }
  };

  const handlePaymentClick = () => {
    onPayment();
    onClose(); // Close drawer after initiating payment
  };

  const handleClearCart = () => {
    if (cartItems.length > 0 && window.confirm(t('pos.confirmClearCart') || 'Clear all items from cart?')) {
      onClearCart();
    }
  };

  // Full screen drawer for phones with enhanced iPhone support
  if (fullScreen) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        className="mobile-cart-drawer"
        PaperProps={{
          className: 'mobile-cart-drawer',
          sx: {
            width: '100%',
            height: '100dvh',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            // Safe area support
            paddingTop: hasSafeArea ? 'env(safe-area-inset-top)' : 0,
            paddingBottom: hasSafeArea ? 'env(safe-area-inset-bottom)' : 0,
            paddingLeft: hasSafeArea ? 'env(safe-area-inset-left)' : 0,
            paddingRight: hasSafeArea ? 'env(safe-area-inset-right)' : 0,
          }
        }}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
        }}
        SlideProps={{
          direction: 'up'
        }}
      >
        {/* Enhanced Full Screen Header with Safe Area */}
        <AppBar 
          position="static" 
          elevation={0}
          className="cart-header"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Toolbar 
            sx={{ 
              px: 2,
              minHeight: '64px !important',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              sx={{ 
                mr: 2,
                width: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBack fontSize="medium" />
            </IconButton>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              {t('pos.currentOrder')} ({cartItems.length})
            </Typography>
            <IconButton 
              color="inherit" 
              onClick={handleClearCart}
              disabled={cartItems.length === 0}
              sx={{
                width: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <ClearAll fontSize="medium" />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Enhanced Full Screen Content */}
        <Box 
          className="cart-content"
          sx={{ 
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Enhanced Cart Items - Scrollable with iPhone optimization */}
          <Box sx={{ 
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 2,
            py: 1,
            // iOS scroll optimization
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            // Custom scrollbar for better mobile experience
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(102, 126, 234, 0.3)',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(102, 126, 234, 0.5)',
            }
          }}>
            {cartItems.length === 0 ? (
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                textAlign: 'center',
                px: 3,
                py: 4
              }}>
                <MotionDiv
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <ShoppingCart sx={{ 
                    fontSize: { xs: 80, sm: 100 }, 
                    color: '#667eea', 
                    opacity: 0.4, 
                    mb: 3
                  }} />
                </MotionDiv>
                <Typography 
                  color="#667eea" 
                  variant="h5" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    fontSize: { xs: '1.3rem', sm: '1.5rem' }
                  }}
                >
                  {t('pos.cartEmpty')}
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    lineHeight: 1.5,
                    maxWidth: '280px'
                  }}
                >
                  {t('pos.cartEmptyDesc') || 'Add some delicious items to get started!'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0, mb: 2 }}>
                {cartItems.map((item, index) => (
                  <MotionDiv
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 2.5,
                      px: 1.5,
                      borderBottom: index < cartItems.length - 1 ? '1px solid rgba(102, 126, 234, 0.1)' : 'none',
                      minHeight: { xs: 100, sm: 110 },
                      borderRadius: 2,
                      mb: 1,
                      background: 'rgba(102, 126, 234, 0.02)',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.05)'
                      },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          color: '#333',
                          fontSize: { xs: '1.1rem', sm: '1.2rem' },
                          lineHeight: 1.3,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: '#667eea', 
                          fontWeight: 700,
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}>
                          {settings?.defaultSettings?.currencySymbol || '$'}{(item.price * item.quantity).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.9rem',
                          mt: 0.5
                        }}>
                          {settings?.defaultSettings?.currencySymbol || '$'}{item.price.toFixed(settings?.defaultSettings?.decimalPlaces || 2)} each
                        </Typography>
                      </Box>
                      
                      {/* Enhanced quantity controls for mobile */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton 
                          size="large" 
                          onClick={() => onDecreaseQuantity(item.id)}
                          sx={{
                            color: '#667eea',
                            background: 'rgba(102, 126, 234, 0.1)',
                            width: { xs: 48, sm: 52 },
                            height: { xs: 48, sm: 52 },
                            borderRadius: 2,
                            '&:hover': {
                              background: 'rgba(102, 126, 234, 0.2)',
                              transform: 'scale(1.05)'
                            },
                            '&:active': {
                              transform: 'scale(0.95)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Remove fontSize="medium" />
                        </IconButton>
                        
                        <Typography sx={{ 
                          fontWeight: 700, 
                          minWidth: '32px', 
                          textAlign: 'center',
                          fontSize: { xs: '1.2rem', sm: '1.3rem' },
                          mx: 1,
                          color: '#333'
                        }}>
                          {item.quantity}
                        </Typography>
                        
                        <IconButton 
                          size="large" 
                          onClick={() => onIncreaseQuantity(item.id)}
                          sx={{
                            color: '#667eea',
                            background: 'rgba(102, 126, 234, 0.1)',
                            width: { xs: 48, sm: 52 },
                            height: { xs: 48, sm: 52 },
                            borderRadius: 2,
                            '&:hover': {
                              background: 'rgba(102, 126, 234, 0.2)',
                              transform: 'scale(1.05)'
                            },
                            '&:active': {
                              transform: 'scale(0.95)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Add fontSize="medium" />
                        </IconButton>
                        
                        <IconButton 
                          onClick={() => onRemoveFromCart(item.id)}
                          sx={{ 
                            ml: 1, 
                            color: '#ef4444',
                            background: 'rgba(239, 68, 68, 0.1)',
                            width: { xs: 48, sm: 52 },
                            height: { xs: 48, sm: 52 },
                            borderRadius: 2,
                            '&:hover': {
                              background: 'rgba(239, 68, 68, 0.2)',
                              transform: 'scale(1.05)'
                            },
                            '&:active': {
                              transform: 'scale(0.95)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <DeleteOutline fontSize="medium" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </MotionDiv>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Full Screen Footer */}
        <Box 
          className="cart-footer"
          sx={{ 
            flexShrink: 0,
            p: 2, 
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: '#f8f9fa'
          }}
        >
          {/* ✅ NEW: Dynamic Add-Ons Options */}
          {enabledAddOns.length > 0 && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {enabledAddOns.map(addOn => (
                <FormControlLabel
                  key={addOn.id}
                  control={
                    <Checkbox 
                      checked={addOnStates[addOn.id] || false}
                      onChange={(e) => handleAddOnToggle(addOn.id, e.target.checked)}
                      sx={{ 
                        color: '#667eea',
                        '&.Mui-checked': { color: '#667eea' }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {addOn.name} ({addOn.type === 'fixed' ? 
                        `${settings?.defaultSettings?.currencySymbol || '$'}${addOn.value.toFixed(settings?.defaultSettings?.decimalPlaces || 2)}` : 
                        `${addOn.value}%`})
                    </Typography>
                  }
                />
              ))}
            </Stack>
          )}

          {/* ✅ NEW: Dynamic Metadata Fields */}
          {enabledMetadataFields.length > 0 && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {enabledMetadataFields.map(([fieldName, fieldConfig]) => (
                <Box key={fieldName}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: '#667eea',
                    fontSize: '0.85rem'
                  }}>
                    {fieldConfig.label}:
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={fieldConfig.placeholder}
                    value={orderMetadata[fieldName] || ''}
                    onChange={(e) => handleMetadataChange(fieldName, e.target.value)}
                    required={fieldConfig.required}
                    size="medium"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getFieldIcon(fieldName)}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        fontSize: '1rem'
                      }
                    }}
                  />
                </Box>
              ))}
            </Stack>
          )}

          {/* Enhanced Totals Summary */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#555', fontSize: '0.9rem' }}>
                {t('pos.subtotal')}:
              </Typography>
              <Typography sx={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
                {settings?.defaultSettings?.currencySymbol || '$'}{totals.subtotal.toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
              </Typography>
            </Box>
            
            {/* ✅ FIXED: Dynamic add-on totals with proper discount handling */}
            {enabledAddOns.map(addOn => {
              if (!addOnStates[addOn.id]) return null;
              const amount = totals[addOn.id] || 0;
              const isDiscount = addOn.type === 'discount';
              
              return (
                <Box key={addOn.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#555', fontSize: '0.9rem' }}>
                    {addOn.name}:
                  </Typography>
                  <Typography sx={{ 
                    fontWeight: 500, 
                    color: isDiscount ? '#ef4444' : '#333', 
                    fontSize: '0.9rem' 
                  }}>
                    {isDiscount ? '-' : ''}{settings?.defaultSettings?.currencySymbol || '$'}{Math.abs(amount).toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
                  </Typography>
                </Box>
              );
            })}
            
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                {t('pos.total')}:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                {settings?.defaultSettings?.currencySymbol || '$'}{totals.total.toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
              </Typography>
            </Box>
          </Stack>

          {/* Payment Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handlePaymentClick}
            disabled={cartItems.length === 0 || !isOnline}
            sx={{
              minHeight: 56,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0px)'
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
                boxShadow: 'none'
              }
            }}
          >
            {t('pos.proceedToPayment')}
          </Button>
        </Box>
      </Drawer>
    );
  }

  // Regular drawer for tablets (if needed) - simplified version
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '85%',
          maxWidth: 400,
          borderRadius: '16px 0 0 16px'
        }
      }}
    >
      <Paper sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff'
      }}>
        {/* Header */}
        <Box sx={{
          ...commonStyles.headerSection,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('pos.currentOrder')}
          </Typography>
          <IconButton color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        
        <Divider />
        
        {/* Content - similar to full screen but adapted for drawer */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Simplified tablet drawer content would go here */}
        </Box>
      </Paper>
    </Drawer>
  );
};

export default MobileCartDrawer;